import { LoginCallback } from '@okta/okta-react';
import { UptimeOAuthErrorMode } from '@uptime/uptime-component';
import { PilotImage, UptimeLogo } from 'assets/images';
import { oktaSignInConfig } from 'config/oktaAuth';
import useGetRoutesConfig from 'hooks/useGetRoutesConfig';
import React, { useEffect, useMemo } from 'react';
import { Redirect, Route, RouteProps, Switch } from 'react-router-dom';
import {
  AssignmentIssuePath,
  CatalogServicePath,
  ChatPath,
  EditPreferencePath,
  GetSupportLandingPath,
  GetSupportPath,
  GetTopKnowledgeArticlePath,
  GlobalSearchPath,
  HomePath,
  IcAppsPath,
  KnowledgeDetail,
  LoginPath,
  ManageDevicesPath,
  ManageOrderPath,
  ManageOutagesPath,
  ManagePath,
  ManagePersonalTechnologyPath,
  ManageRequestVAPath,
  ManageRequestVMPath,
  MarketplacePath,
  NewJoinerPath,
  NotFoundPath,
  OnboardingPath,
  OffboardingPath,
  PcLostAndStolenPath,
  PcRefreshPath,
  PcRegisterDevicePath,
  PcReportBroken,
  PcReturnAnyDevicePath,
  PcReturnPath,
  PilotModePath,
  AnalyticsPagePath,
  CustomDashboardPagePath,
  ExperienceCustomDashboardPath,
  ManageIncidentTicketPath,
  ManageRequestTicketPath,
  ServicesPath,
  PowerBIDashboardPath,
  ExperienceDashboardPath,
  LastAccessPath,
  NewsAndKnowledgePath,
  AutomatedServiceRequestPath
} from 'utils/routes';
import { ProtectedRoute } from './AuthComponent';
import MarketplaceRoutes, { IMarketplaceRoutesProps, marketplaceRoutes } from './MarketplaceRoutes';
import { useMarketplaceProvider } from 'containers/MarketplaceContainer';
import useNewJoinerOrderTask from 'pages/OnboardingPage/hooks/useNewJoinerOrderTask';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { handleCheckCustomRoutes, handleCheckLastDate } from 'utils/function';
import { TRACKING_PATH } from 'constants/index';
import PageTitle from 'components/PageTitle';

const MarketPlacePage = React.lazy(() => import('pages/MarketPlacePage'));
const RequestPage = React.lazy(() => import('pages/RequestPage'));
const IncidentPage = React.lazy(() => import('pages/IncidentPage'));
const AssignmentIssuePage = React.lazy(() => import('pages/AssignmentIssuePage'));
const AutomatedServiceRequestPage = React.lazy(() => import('pages/AutomatedServiceRequestPage'));
const CatalogServicePage = React.lazy(() => import('pages/CatalogPages/CatalogServicePage'));
const EditProfilePage = React.lazy(() => import('pages/EditProfilePage'));
const GetSupportSearchPage = React.lazy(() => import('pages/GetSupportSearchPage'));
const GetTopKnowledgePage = React.lazy(() => import('pages/GetTopKnowledgePage'));
const IcAppsPage = React.lazy(() => import('pages/IcAppsPage'));
const KnowledgeDetailPage = React.lazy(() => import('pages/GetSupportSearchPage/KnowledgeArticleList/KnowledgeDetail'));
const LoginPage = React.lazy(() => import('pages/Login'));
const ManageDevicesPage = React.lazy(() => import('pages/ManageDevicesPage'));
const OnboardingPage = React.lazy(() => import('pages/OnboardingPage'));
const OffboardingPage = React.lazy(() => import('pages/OffboardingPage'));
const ManagePage = React.lazy(() => import('pages/ManagePage'));
const NewJoinerPage = React.lazy(() => import('pages/NewJoinerPage'));
const OutagesPage = React.lazy(() => import('pages/OutagesPage'));
const PCOrderPage = React.lazy(() => import('pages/PCOrderPage'));
const PCRefreshPage = React.lazy(() => import('pages/PCRefreshPage'));
const PCRegisterDevicePage = React.lazy(() => import('pages/PCRegisterDevicePage'));
const PCReturnAnyDevicePage = React.lazy(() => import('pages/PCReturnAnyDevicePage'));
const PageNotFoundPage = React.lazy(() => import('pages/PageNotFoundPage'));
const PcLostAndStolenPage = React.lazy(() => import('pages/PcLostAndStolenPage'));
const PcReportBrokenPage = React.lazy(() => import('pages/PcReportBrokenPage'));
const PcReturnPage = React.lazy(() => import('pages/PcReturnPage'));
const PilotPage = React.lazy(() => import('pages/PilotPage'));
const RequestVAPage = React.lazy(() => import('pages/RequestVAPage'));
const RequestVMPage = React.lazy(() => import('pages/RequestVMPage'));
const TechnologyPage = React.lazy(() => import('pages/TechnologyPage'));
const GlobalSearchPage = React.lazy(() => import('pages/GlobalSearchPage'));
const DashboardPage = React.lazy(() => import('pages/DashboardPage'));
const AnalyticsPage = React.lazy(() => import('pages/AnalyticsPage'));
const CustomDashboardPage = React.lazy(() => import('pages/DashboardPage/CustomeDashboardPage'));
const ServicesPage = React.lazy(() => import('pages/ServicesPage'));
const LastAccessPage = React.lazy(() => import('pages/LastAccessPage'));
const NewsAndKnowledgePage = React.lazy(() => import('pages/NewsAndKnowledgePage'));
const DynamicHomepage = React.lazy(() => import('pages/HomePage/Homepage'));
const SupportLadingPage = React.lazy(() => import('pages/SupportLandingPage'));

export interface RouterProviderProps {
  isAuthenticatedByOkta: boolean;
  isPilotMode: boolean;
  isNewJoiner: boolean;
  setShowFeedback: any;
  isLastWorkingDay: boolean;
  isOverLastAccessDate: boolean;
  isBeforeStartAccessDate: boolean;
}

export interface RouteConfig extends RouteProps {
  name?: string;
  enable?: boolean;
  isProtected?: boolean;
  isMarketplaceRoute?: boolean;
  oldPath?: any;
  permalinkPath?: string;
  trackingPath?: string;  
}

export interface InternalRouteProps {
  name: string;
  permalinkPath: string;
  internalPath: string;
  oldPath: any;
}

export interface CheckRoutesConfigProps {
  hasNewRoutesPath: boolean;
  newPath: any;
  trackingPath: any;
}

export const RouterProvider = (props: RouterProviderProps) => {
  let routes = useRoutes(props);
  const {routes: getListRoutesConfig, routesConfig }: any = useGetRoutesConfig();
  const whiteListRoutes = getListRoutesConfig || [];
  const { isOrderAllowed } = useNewJoinerOrderTask();
  
  routes = routes.map((item) => {
    const matchingRoute = routesConfig?.find((config: InternalRouteProps) => config.name === item.name);

    if (!!matchingRoute) {
      return { ...item, permalinkPath: matchingRoute.permalinkPath, path: matchingRoute.internalPath, oldPath: matchingRoute.oldPath };
    }

    return item;
  });

  const checkCustomRoutes: CheckRoutesConfigProps | undefined = handleCheckCustomRoutes(routes);

  function getCoreRoute(path: string) {
    const coreRoute = routes.find(route => path === route.name);
    if (!coreRoute) {
      throw Error(
        `${path} is not exist in core, please make sure you are importing right path, or need add custom component`,
      );
    }

    return coreRoute;
  }

  function routerMapping() {
    return whiteListRoutes.map((r: string) => {
      return getCoreRoute(r);
    }) as RouteConfig[];
  }

  const mappedRoutes = whiteListRoutes.length > 0 ? routerMapping() : routes;
  const enabledRoutes = mappedRoutes.filter(route => route.enable !== false && !route.isMarketplaceRoute);
  const marketplaceRoutes = mappedRoutes.filter(route => route.enable !== false && !!route.isMarketplaceRoute);

  useEffect(() => {
    const { hasNewRoutesPath, trackingPath } = checkCustomRoutes;
    if (hasNewRoutesPath && !!trackingPath) {
      localStorage.setItem(TRACKING_PATH, JSON.stringify(trackingPath));
    }
  }, [checkCustomRoutes]);

  return (
    <Switch>
      {enabledRoutes.map(route =>
        route.isProtected ? (
          <ProtectedRoute key={String(route.path)} {...route} />
        ) : (
          <Route key={String(route.path)} {...route} />
        ),
      )}
      {marketplaceRoutes.length > 0 ? <MarketplaceRoutes routes={marketplaceRoutes} isOrderAllowed={isOrderAllowed} checkCustomRoutes={checkCustomRoutes} /> : ""}
      <Redirect to={checkCustomRoutes?.hasNewRoutesPath ? checkCustomRoutes?.newPath : NotFoundPath} />
    </Switch>
  );
};

export const useRoutes = (routerProps: RouterProviderProps) => {
  const {
    isAuthenticatedByOkta,
    isPilotMode,
    setShowFeedback,
    isBeforeStartAccessDate
  } = routerProps;
  const { isOnBehalfNewJoiner, isOnBehalfLeaver, userOnBehalf } = useSelector(
    (state: RootState) => state.onBehalfReducer,
  );
  const { generalConfig } = useSelector((state: RootState) => state.configReducer);
  const isLeaverEnabled = generalConfig?.leaver?.isLeaverEnabled;

  let { isNewJoiner, isLastWorkingDay, isOverLastAccessDate } = routerProps;
  isNewJoiner = isOnBehalfNewJoiner ? isOnBehalfNewJoiner : isNewJoiner;
  isLastWorkingDay = isOnBehalfLeaver ? handleCheckLastDate(userOnBehalf, 'last_working_date') : isLastWorkingDay;
  isOverLastAccessDate = isOnBehalfLeaver ? false : isOverLastAccessDate;
  const userAccessPermission = isPilotMode && !isNewJoiner && !isLastWorkingDay && !isOverLastAccessDate && !isBeforeStartAccessDate;
  const newJoinerAccessPermission = isPilotMode && isNewJoiner && !isBeforeStartAccessDate;
  const offBoardingPermission = isPilotMode && isLastWorkingDay && !isOverLastAccessDate;
  
  const exact = true;
  const marketplaceContext = useMarketplaceProvider();
  const routes: RouteConfig[] = useMemo(
    () => [
      {
        name: 'LoginCallBackPath',
        path: '/login/callback',
        render: (props: any) => (
          <LoginCallback
            {...props}
            errorComponent={(props: any) => (
              <>
                <PageTitle title=''/>              
                <UptimeOAuthErrorMode
                  {...props}
                  logoImg={UptimeLogo}
                  mainImg={PilotImage}
                  loginPath={LoginPath}
                />
              </>
            )}
          />
        ),
        enable: isAuthenticatedByOkta,
      },
      {
        name: 'LoginPath',
        path: LoginPath,
        render: (secureProps: any) => (
          <LoginPage {...secureProps} config={oktaSignInConfig} />
        ),
        exact,
      },
      {
        name: 'PilotModePath',
        path: PilotModePath,
        render: (secureProps: any) => <PilotPage {...secureProps} />,
        exact,
      },
      {
        name: 'HomePath',
        path: HomePath,
        component: DynamicHomepage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ChatPath',
        path: ChatPath,
        component: DynamicHomepage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ManageOutagesPath',
        path: ManageOutagesPath,
        component: OutagesPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ManageIncidentTicketPath',
        path: ManageIncidentTicketPath,
        component: IncidentPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ManageRequestTicketPath',
        path: ManageRequestTicketPath,
        component: RequestPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ManagePersonalTechnologyPath',
        path: ManagePersonalTechnologyPath,
        component: TechnologyPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ManageOrderPath',
        path: ManageOrderPath,
        component: PCOrderPage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'ManageRequestVMPath',
        path: ManageRequestVMPath,
        component: RequestVMPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ManageRequestVAPath',
        path: ManageRequestVAPath,
        component: RequestVAPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'KnowledgeDetail',
        path: KnowledgeDetail,
        component: KnowledgeDetailPage,
        enable: isPilotMode,
        exact,
        isProtected: true,
      },
      {
        name: 'GetSupportLandingPath',
        path: GetSupportLandingPath,
        component: SupportLadingPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'GetSupportPath',
        path: GetSupportPath,
        component: GetSupportSearchPage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'GetTopKnowledgeArticlePath',
        path: GetTopKnowledgeArticlePath,
        component: GetTopKnowledgePage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'NewsAndKnowledgePath',
        path: NewsAndKnowledgePath,
        component: NewsAndKnowledgePage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'PcRefreshID',
        path: `${PcRefreshPath}/:deviceID`,
        component: PCRefreshPage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'AssignmentIssueID',
        path: `${AssignmentIssuePath}/:deviceID`,
        component: AssignmentIssuePage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'PcReturnPathID',
        path: `${PcReturnPath}/:deviceID`,
        component: PcReturnPage,
        enable: isPilotMode && !isNewJoiner,
        isProtected: true,
      },
      {
        name: 'PcReturnPath',
        path: PcReturnPath,
        component: PcReturnPage,
        enable: isPilotMode && !isNewJoiner,
        isProtected: true,
      },
      {
        name: 'PcLostAndStolenPathID',
        path: `${PcLostAndStolenPath}/:deviceID`,
        component: PcLostAndStolenPage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'PcReportBrokenID',
        path: `${PcReportBroken}/:deviceID`,
        component: PcReportBrokenPage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'PcReturnAnyDevicePath',
        path: PcReturnAnyDevicePath,
        component: PCReturnAnyDevicePage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'PcRegisterDevicePath',
        path: PcRegisterDevicePath,
        component: PCRegisterDevicePage,
        enable: userAccessPermission,
        isProtected: true,
      },
      {
        name: 'EditPreferencePath',
        path: EditPreferencePath,
        component: EditProfilePage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ManageDevicesPath',
        path: ManageDevicesPath,
        component: ManageDevicesPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'CatalogServicePath',
        path: CatalogServicePath,
        component: CatalogServicePage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'AutomatedServiceRequestPath',
        path: AutomatedServiceRequestPath,
        component: AutomatedServiceRequestPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'IcAppsPath',
        path: IcAppsPath,
        component: IcAppsPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ManagePath',
        path: ManagePath,
        component: ManagePage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'OnboardingPath',
        path: OnboardingPath,
        component: OnboardingPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'OffboardingPath',
        path: OffboardingPath,
        component: OffboardingPage,
        enable: isPilotMode && !isNewJoiner,
        exact,
        isProtected: true,
      },
      {
        name: 'ServicesPath',
        path: ServicesPath,
        component: ServicesPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'AnalyticsPagePath',
        path: AnalyticsPagePath,
        component: AnalyticsPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'PowerBIDashboardPath',
        path: PowerBIDashboardPath,
        component: DashboardPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ExperienceDashboardPath',
        path: ExperienceDashboardPath,
        component: DashboardPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'CustomDashboardPagePath',
        path: `${CustomDashboardPagePath}/:name`,
        component: CustomDashboardPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'ExperienceCustomDashboardPath',
        path: `${ExperienceCustomDashboardPath}/:name`,
        component: CustomDashboardPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      ...marketplaceRoutes.map((i: IMarketplaceRoutesProps) => ({
        ...i,
        name: i.name,
        isMarketplaceRoute: true,
        isProtected: true,
        enable:
          isPilotMode && marketplaceContext?.isSupportCountry,
      })),
      {
        name: 'MarketplacePath',
        exact: true,
        path: MarketplacePath,
        component: MarketPlacePage,
        enable: isPilotMode && !isBeforeStartAccessDate,
      },
      {
        name: 'NewJoinerPath',
        path: NewJoinerPath,
        component: NewJoinerPage,
        enable: newJoinerAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'NotFoundPath',
        path: NotFoundPath,
        render: () => <PageNotFoundPage setShowFeedback={setShowFeedback} />,
      },
      {
        name: 'GlobalSearchPath',
        path: GlobalSearchPath,
        component: GlobalSearchPage,
        enable: userAccessPermission,
        exact,
        isProtected: true,
      },
      {
        name: 'LastAccessPath',
        path: LastAccessPath,
        component: LastAccessPage,
        enable: offBoardingPermission,
        exact,
        isProtected: true,
      },
    ],
    [isPilotMode, isNewJoiner, isLeaverEnabled, marketplaceContext?.isSupportCountry, isLastWorkingDay, isOverLastAccessDate, isBeforeStartAccessDate],
  );

  return routes;
};
